// The scheme-apply field grid, in one place.
//
// Every field row in the v1 engine is the same 1/2/3-up responsive grid. It was
// expressed as `w-full md:w-[calc(50%-8px)] lg:w-[calc(33.3333%-10.667px)]`,
// where 8px and 10.667px are hand-computed gap compensation for a 16px gutter
// ((100% - 2*16px)/3 == 33.3333% - 10.667px). CSS Grid derives those widths
// from the gap instead, so the constants disappear and retuning COLUMN_GAP
// cannot desynchronise them.
const COLUMN_GAP = 2; // theme.spacing(2) = 16px — the Figma field-grid gutter
const ROW_GAP = 4; // theme.spacing(4) = 32px

export const FIELD_GRID_SX = {
  display: "grid",
  columnGap: COLUMN_GAP,
  rowGap: ROW_GAP,
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
  alignItems: "stretch",
};

// Grid children stretch by default, so a cell needs no width — only the
// full-bleed ones need to say so.
export const FULL_WIDTH_CELL_SX = { gridColumn: "1 / -1" };

// A focused control paints 5px outside its border box (3px ring + 2px offset,
// rewampTheme.js), so any container holding one needs at least that inset.
export const CONTROL_INSET = 1;

export const cellSxFor = (isFullWidth) => (isFullWidth ? FULL_WIDTH_CELL_SX : undefined);

export const isMultiColumnGridCell = (cellSx) => cellSx?.gridColumn !== "1 / -1";

export const GRID_ALIGNED_CELL_ATTR = "data-grid-aligned-cell";
export const GRID_FIELD_STACK_ATTR = "data-grid-field-stack";
export const GRID_FIELD_LABEL_ATTR = "data-grid-field-label";
export const GRID_LF_SPACER_ATTR = "data-grid-lf-spacer";
export const GRID_FH_SPACER_ATTR = "data-grid-fh-spacer";

export const FIELD_GRID_CELL_SX = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
};

export const FIELD_GRID_CELL_SHRINK_SX = {
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
};

export const fieldGridCellSx = (fillCell = true) =>
  fillCell ? FIELD_GRID_CELL_SX : FIELD_GRID_CELL_SHRINK_SX;

// Spec: label -> 4px -> field -> 4px -> helper. The unaligned stack gets both
// from its own `gap` (it collapses to nothing when a slot is empty); the
// aligned stack has no gap token, so each slot carries its own margin.
export const LABEL_GAP = 0.5;

// field -> helper. Mirrors LABEL_GAP; kept separate so the two can diverge.
export const FIELD_HELPER_GAP = 0.5;
// Raw-px twin of FIELD_HELPER_GAP for gridRowHelperSync.js, which runs pure
// DOM measurement with no theme instance to call theme.spacing() on.
export const FIELD_HELPER_GAP_PX = 4;

export const FIELD_GRID_LABEL_SX = { flexShrink: 0, mb: LABEL_GAP };

// lf-spacer: label→control alignment only — height set by row sync, never flex-grow.
export const FIELD_GRID_LF_SPACER_SX = {
  flexGrow: 0,
  flexShrink: 0,
  flexBasis: "auto",
  minHeight: 0,
};

export const FIELD_GRID_CONTROL_SX = { flexShrink: 0, width: "100%" };

// fh-spacer: flex-grow sink below control when stack fills the cell; row sync sets minHeight.
// pt + flex-start is the actual field->helper gap: it must sit right after
// the padding regardless of how tall row-sync/flex-grow makes this box, or
// leftover height (very common — a short control in a row stretched to match
// a taller sibling) lands ABOVE the text instead of being harmless trailing
// space below it.
export const fieldGridFhSpacerSx = (theme, { fillCell = true } = {}) => ({
  flex: fillCell ? "1 1 0" : "0 0 auto",
  // caption.lineHeight is a unitless ratio, not a length — `calc(1.3 + 4px)`
  // is invalid CSS and gets dropped silently, so it must be multiplied by
  // fontSize first to become an actual length before adding the gap.
  minHeight: `calc(${theme.typography.caption.fontSize} * ${theme.typography.caption.lineHeight} + ${theme.spacing(FIELD_HELPER_GAP)})`,
  pt: FIELD_HELPER_GAP,
  minWidth: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
});
